"use client";

import { Globe, Play, WifiOff } from "lucide-react";
import Link from "@/components/ui/Link";
import { useEffect, useState } from "react";
import { useUi } from "@/components/ui/use-ui";
import { fmt } from "@/lib/locales/fmt";

const box = "theme-dark relative aspect-video w-full overflow-hidden bg-black max-md:-mx-3 max-md:w-[calc(100%+1.5rem)] md:rounded-xl";

/**
 * A tube site's own player (Pornhub) for videos listed from its webmaster feed
 * (lib/frame-sources.ts). Until it's started only the poster shows, so the page loads nothing from the
 * site; then its player takes the whole box, as it is (their terms forbid changing or covering it), and
 * starts playing. A view counts when it starts. `loaded`: the player is in the page from the start, not
 * playing, for search engines' verified crawlers, which find a video only in an <iframe> they don't have to
 * click for (developers.google.com/search/docs/appearance/video); it's what a visitor gets on clicking the poster.
 */
export function FramePlayer({ videoId, src, poster, title, label, loaded = false }: { videoId: string; src: string; poster?: string | null; title: string; label?: string; loaded?: boolean }) {
  const [started, setStarted] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const t = useUi();

  // Its player comes straight from the site's servers: where a network or a country blocks the site, the box stays
  // blank. Asked once it starts (a request the browser makes anyway): if the site doesn't answer, a line under the
  // player says why, rather than leaving people to think Sweet is broken.
  useEffect(() => {
    if (!started) return;
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 8000);
    fetch(`${new URL(src).origin}/favicon.ico`, { mode: "no-cors", cache: "no-store", signal: ctrl.signal })
      .catch(() => setUnreachable(true))
      .finally(() => window.clearTimeout(timer));
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [started, src]);

  if (started || loaded)
    return (
      <>
      <div className={box}>
        <iframe
          src={started ? `${src}${src.includes("?") ? "&" : "?"}autoplay=1` : src}
          title={title}
          // "*": the player may land on another of the site's domains (XVideos sends some countries to
          // xv-ru.com), where a bare "fullscreen" (that of the src's domain only) would block its button.
          allow="autoplay *; fullscreen *; picture-in-picture *; encrypted-media *"
          allowFullScreen
          className="absolute inset-0 size-full border-0"
        />
      </div>
      {unreachable && (
        <p role="status" className="mt-2 flex items-start gap-2 rounded-xl border border-line bg-raised px-3.5 py-2.5 text-[13px] leading-relaxed text-soft">
          <WifiOff size={15} className="mt-0.5 shrink-0 text-brand-soft" aria-hidden />
          {fmt(t.video.partnerBlocked, { name: label ?? t.player.partner })}
        </p>
      )}
      </>
    );
  return (
    <button
      type="button"
      aria-label={fmt(t.video.play, { title })}
      onClick={() => {
        setStarted(true);
        navigator.sendBeacon?.(`/api/videos/${videoId}/view`);
      }}
      className={`${box} group grid place-items-center`}
    >
      {poster && (
        // eslint-disable-next-line @next/next/no-img-element -- the site's own thumbnail CDN
        <img src={poster} alt="" fetchPriority="high" className="absolute inset-0 size-full object-cover" />
      )}
      <span className="glass relative grid size-[72px] place-items-center rounded-full text-white transition-transform group-hover:scale-105 md:size-20">
        <Play size={32} className="translate-x-0.5 fill-white" />
      </span>
      <span className="thumb-text absolute bottom-3 right-4 flex items-center gap-1.5 text-[12.5px] text-white/85">
        <Globe size={13} /> {fmt(t.video.playsIn, { name: label ?? t.player.partner })}
      </span>
    </button>
  );
}

/** In place of the player where the site doesn't play its videos (lib/frame-sources blockedCountries). */
export function FrameUnavailable({ poster, label }: { poster?: string | null; label?: string }) {
  const t = useUi();
  return (
    <div className={`${box} grid place-items-center`}>
      {poster && (
        // eslint-disable-next-line @next/next/no-img-element -- the site's own thumbnail CDN
        <img src={poster} alt="" className="absolute inset-0 size-full object-cover opacity-30 blur-sm" />
      )}
      <div className="glass relative mx-4 max-w-md rounded-xl px-5 py-4 text-center text-[14px] leading-relaxed text-soft">
        {fmt(t.video.unavailable, { name: label ?? t.player.partner })}{" "}
        <Link href="/" className="text-brand-soft hover:underline">
          {t.video.watchCams}
        </Link>
      </div>
    </div>
  );
}
